import { useEffect, useRef } from "react";
import { useShallow } from "zustand/react/shallow";

import { getChartSourceSnapshot } from "@/lib/lytick/chart-source-service";
import { getBaseColumns } from "@/lib/lytick/chart-data-service";
import { sliceToCandles, searchTime } from "@/lib/lytick/columnar";
import { useChartStore } from "@/stores/chartStore";
import { useReplayStore } from "@/stores/replayStore";

/**
 * CSV replay backfill: when the replay store enters "loading" state, immediately
 * resolve the dataset from the already-loaded CSV candles in chart-source-service.
 * No network fetch — the entire dataset is already in memory from the file upload.
 */
export function useReplayBackfill() {
  const { sessionState, startDate, endDate, sourceGranularitySeconds } = useReplayStore(
    useShallow((state) => ({
      sessionState: state.sessionState,
      startDate: state.startDate,
      endDate: state.endDate,
      sourceGranularitySeconds: state.sourceGranularitySeconds,
    })),
  );
  const symbol = useChartStore((state) => state.symbol);

  const loadingRef = useRef(false);

  useEffect(() => {
    if (sessionState !== "loading" || loadingRef.current) return;
    if (!startDate || !endDate) return;

    loadingRef.current = true;

    try {
      const snapshot = getChartSourceSnapshot();
      const granularity = snapshot.granularitySeconds || sourceGranularitySeconds || 60;

      useReplayStore.getState().setLoadProgress(50);
      useReplayStore.getState().setReplayData(startDate, endDate, granularity);
      useReplayStore.getState().setLoadProgress(100);

      // Use CSV candles before the replay start as context
      const base = getBaseColumns();
      if (base && base.length > 0) {
        const cutIdx = searchTime(base, startDate);
        const contextCandles = sliceToCandles(base, 0, cutIdx);
        if (contextCandles.length > 0) {
          useReplayStore.getState().setContextCandles(contextCandles, granularity);
        }
      }
    } catch (err) {
      console.error("Replay dataset build failed", err);
      useReplayStore.getState().setErrorMessage(
        err instanceof Error ? err.message : "Failed to load replay data",
      );
    } finally {
      loadingRef.current = false;
    }
  }, [sessionState, startDate, endDate, symbol, sourceGranularitySeconds]);
}
